import React from 'react';
import { useNavigate } from 'react-router-dom';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { SocialMediaUpload } from '../../../shared/services/socialMediaService';
import IconButton from '../../../shared/components/ui/IconButton';

interface SocialMediaUploadsTableProps {
  uploads: SocialMediaUpload[];
  isLoading: boolean;
  onRowClick: (uploadId: string) => void;
  onViewClick: (uploadId: string) => void;
  onDeleteClick: (uploadId: string) => void;
  searchQuery?: string;
}

const SocialMediaUploadsTable: React.FC<SocialMediaUploadsTableProps> = ({
  uploads,
  isLoading,
  onRowClick,
  onViewClick,
  onDeleteClick,
  searchQuery = '',
}) => {
  const navigate = useNavigate();
  const location = window.location;
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = orgIdFromPath || '';

  const formatDate = (iso?: string): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-800 border border-gray-200',
      queued: 'bg-gray-100 text-gray-800 border border-gray-200',
      uploading: 'bg-blue-100 text-blue-800 border border-blue-200',
      processing: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      completed: 'bg-green-100 text-green-800 border border-green-200',
      failed: 'bg-red-100 text-red-800 border border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border border-gray-200',
    };
    return styles[status as keyof typeof styles] || styles.pending;
  };

  const getPlatformBadge = (platform: string) => {
    const colors: Record<string, string> = {
      youtube: 'bg-red-100 text-red-800 border border-red-200',
      instagram: 'bg-pink-100 text-pink-800 border border-pink-200',
      tiktok: 'bg-black text-white border border-gray-800',
      facebook: 'bg-blue-100 text-blue-800 border border-blue-200',
      twitter: 'bg-blue-100 text-blue-800 border border-blue-200',
    };
    return colors[platform.toLowerCase()] || 'bg-gray-100 text-gray-800 border border-gray-200';
  };

  const EyeIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

  // Filter uploads by search query
  const filteredUploads = searchQuery.trim()
    ? uploads.filter((upload) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          upload.metadata?.title?.toLowerCase().includes(searchLower) ||
          upload.highlight_id?.toLowerCase().includes(searchLower) ||
          upload.video_id?.toLowerCase().includes(searchLower) ||
          upload.platform?.toLowerCase().includes(searchLower)
        );
      })
    : uploads;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (filteredUploads.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg">
        <p className="font-medium text-gray-700 mb-1">
          {searchQuery.trim() ? 'No uploads match your search' : 'No uploads found'}
        </p>
        <p className="text-sm">
          {searchQuery.trim() ? 'Try a different search term' : 'Uploads will appear here once created'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2 text-left w-24">Entity Type</th>
              <th className="px-3 py-2 text-left min-w-[300px]">Entity Name</th>
              <th className="px-3 py-2 text-left w-24">Match</th>
              <th className="px-3 py-2 text-left w-28">Platform</th>
              <th className="px-3 py-2 text-left w-32">Account</th>
              <th className="px-3 py-2 text-left w-24">Status</th>
              <th className="px-3 py-2 text-left w-40">Uploaded At</th>
              <th className="px-3 py-2 text-left w-24">Platform URL</th>
              <th className="px-3 py-2 text-right w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm">
            {filteredUploads.map((upload) => {
              const entityType = upload.highlight_id ? 'Highlight' : 'Video';
              const entityId = upload.highlight_id || upload.video_id || '';
              const entityName = upload.metadata?.title || `${entityType} ${entityId.slice(0, 8)}`;

              return (
                <tr
                  key={upload.id}
                  onClick={() => onRowClick(upload.id)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-3 py-3">
                    <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 border border-gray-200 whitespace-nowrap">
                      {entityType}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (upload.highlight_id) {
                          navigate(`/o/${orgId}/capture/highlight/${upload.highlight_id}`);
                        } else if (upload.video_id) {
                          navigate(`/o/${orgId}/capture/video/${upload.video_id}`);
                        }
                      }}
                      className="text-blue-600 hover:text-blue-700 hover:underline font-medium text-left break-words"
                      title={entityName}
                    >
                      {entityName}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-gray-500 whitespace-nowrap">
                    {upload.match_id ? upload.match_id.slice(0, 8) + '...' : '—'}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs capitalize whitespace-nowrap ${getPlatformBadge(upload.platform)}`}>
                      {upload.platform}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-sm text-gray-700 whitespace-nowrap">
                      {upload.social_media_integrations?.display_name || 
                       upload.social_media_integrations?.platform_account_name || 
                       '—'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${getStatusBadge(upload.upload_status)}`}>
                      {upload.upload_status.charAt(0).toUpperCase() + upload.upload_status.slice(1)}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-gray-500 whitespace-nowrap">
                    {formatDate(upload.upload_completed_at || upload.created_at)}
                  </td>
                  <td className="px-3 py-3">
                    {upload.platform_url ? (
                      <a
                        href={upload.platform_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <IconButton
                        variant="primary"
                        size="sm"
                        title="View details"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewClick(upload.id);
                        }}
                      >
                        <EyeIcon />
                      </IconButton>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        title="Delete upload"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteClick(upload.id);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </IconButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SocialMediaUploadsTable;

